// eslint-disable-next-line import/no-unresolved -- exports-only subpath; tsc, jest and webpack all resolve it, the import resolver just doesn't read package `exports`
import { formatDate as formatDateCore, formatTimeOfDay } from '@hqo/date-time/core';

// A 4-digit, locale-ordered numeric date (moment's `L`, e.g. en `08/11/2020`, de `11.08.2020`,
// hu `2025. 01. 15.`). `dateStyle: 'short'` would 2-digit the year in several locales, so the
// component bag is the faithful replacement.
const NUMERIC_DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

export const formatMonthDayYear = (value: string, locale: string): string =>
  formatDateCore(value, { locale, options: NUMERIC_DATE_OPTIONS });

export const formatDate = (value: string | Date, timezone: string | undefined, locale: string): string | null =>
  value ? formatDateCore(value, { locale, timezone, dateStyle: 'long' }) : null;

// Time of day in the reader's locale. `@hqo/date-time` follows each locale's clock and CLDR
// conventions — 24-hour for de/nl/etc., 12-hour for en/en-AU/en-CA, and the French `15 h 30`
// separator for fr-FR/fr-CA — where moment's `LT` rendered French as `15:30`.
export const formatTime = (value: string | Date, timezone: string, locale: string): string =>
  formatTimeOfDay(value, { locale, timezone });

export const formatTimeLabel = (dateTime: Date, locale: string): string => formatTimeOfDay(dateTime, { locale });

// The machine VALUE of a picker option, compared against the fixed `DAY_START_TIME` /
// `DAY_FINAL_TIME` constants, so it must be identical `HH:mm` on every host. The hour and minute are
// read as parts and the separator written here, because a host locale writes its own — fr-CA
// `23 h 45`, fi-FI and da-DK `23.45`, ar-EG `٢٣:٤٥` — and the `=== '23:45'` lookup would miss.
const TIME_VALUE_OPTIONS: Intl.DateTimeFormatOptions = { hourCycle: 'h23', hour: '2-digit', minute: '2-digit' };

export const formatTimeValue = (dateTime: Date, timeZone?: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', { ...TIME_VALUE_OPTIONS, timeZone }).formatToParts(dateTime);
  const part = (type: 'hour' | 'minute'): string => parts.find(candidate => candidate.type === type)?.value ?? '00';

  return `${part('hour').padStart(2, '0')}:${part('minute').padStart(2, '0')}`;
};
